import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import api, { extractError } from '../lib/api';
import { useAuth } from '../lib/auth';
import FillQuestionnairePage from './FillQuestionnairePage';

/**
 * Löst einen Direktlink (/fill/:token) auf. Der Endpoint ist nicht öffentlich:
 * Ohne Anmeldung leitet die Auth-Weiterleitung zuerst zum Login (mit returnTo),
 * erst danach wird der Token aufgelöst und der Zugriff geprüft.
 */
export default function DirectFillPage() {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated } = useAuth();

  const { data, isLoading, error } = useQuery<{ id: string }>({
    queryKey: ['direct-fill', token],
    queryFn: async () => {
      const res = await api.get(`/questionnaires/fill/direct/${token}`);
      return res.data;
    },
    enabled: !!token && isAuthenticated,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Nach erfolgreicher Auflösung das bestehende Fragebogen-Formular verwenden.
  if (!error && data?.id) {
    return <FillQuestionnairePage questionnaireId={data.id} />;
  }

  if (!error && (isLoading || !isAuthenticated)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const message = error
    ? extractError(error).message
    : 'Der Link ist ungültig oder wurde deaktiviert.';

  return (
    <div className="flex items-center justify-center py-16">
      <div className="card text-center py-12 max-w-md">
        <AlertCircle size={40} className="mx-auto text-red-400 mb-3" />
        <p className="text-red-500 font-medium">Fragebogen nicht verfügbar</p>
        <p className="text-sm text-gray-500 mt-2">{message}</p>
        <Link to="/" className="btn-secondary mt-4 inline-flex">Zur Übersicht</Link>
      </div>
    </div>
  );
}
